import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ActionSheetHost } from "@/components/ui/action-sheet";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell, { MorePage } from "@/components/AppShell";
import AdminShell, { AccessDenied } from "@/components/admin/AdminShell";
import { Login } from "@/pages/Auth";
import DesignPage from "@/pages/DesignPage";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import MergeCustomers from "@/pages/MergeCustomers";
import ProjectDetail from "@/pages/ProjectDetail";
import Pipeline from "@/pages/Pipeline";
import Invoices from "@/pages/Invoices";
import Reports from "@/pages/Reports";
import Dashboard from "@/pages/Dashboard";
import QuoteEditor from "@/pages/QuoteEditor";
import Quotes from "@/pages/Quotes";
import Settings from "@/pages/Settings";
import Products from "@/pages/Products";
import Billing from "@/pages/Billing";
import Team from "@/pages/Team";
import Account from "@/pages/Account";
import { BillingCancel, BillingSuccess } from "@/pages/BillingReturn";
import AdminCredits from "@/pages/admin/AdminCompany";
import AdminPacks from "@/pages/admin/AdminPacks";
import AdminCosts from "@/pages/admin/AdminCosts";

const isAdmin = (u) => u?.role === "superadmin";

function Loading() {
  return (
    <div data-testid="app-loading" className="grid min-h-screen place-items-center bg-white">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-700 border-t-amber-400" />
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return isAdmin(user) ? <Navigate to="/admin" replace /> : children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return isAdmin(user) ? children : <AccessDenied />;
}

function OwnerOnly({ children }) {
  const { user } = useAuth();
  return user?.role === "owner" ? children : <Navigate to="/dashboard" replace />;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  return user ? <Navigate to={isAdmin(user) ? "/admin" : "/dashboard"} replace /> : children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PublicOnly><Navigate to="/login" replace /></PublicOnly>} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/signup" element={<Navigate to="/login" replace />} />
            <Route element={<Protected><AppShell /></Protected>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/quotes" element={<Quotes />} />
              <Route path="/design" element={<DesignPage />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/customers/:id" element={<CustomerDetail />} />
              <Route path="/customers/:id/merge" element={<MergeCustomers />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/pipeline" element={<Pipeline />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/quotes/new" element={<QuoteEditor key="new" />} />
              <Route path="/quotes/:id" element={<QuoteEditor />} />
              <Route path="/settings" element={<OwnerOnly><Settings /></OwnerOnly>} />
              <Route path="/products" element={<OwnerOnly><Products /></OwnerOnly>} />
              <Route path="/team" element={<OwnerOnly><Team /></OwnerOnly>} />
              <Route path="/account" element={<Account />} />
              <Route path="/more" element={<MorePage />} />
              <Route path="/billing" element={<OwnerOnly><Billing /></OwnerOnly>} />
              <Route path="/billing/success" element={<OwnerOnly><BillingSuccess /></OwnerOnly>} />
              <Route path="/billing/cancel" element={<OwnerOnly><BillingCancel /></OwnerOnly>} />
            </Route>
            <Route element={<AdminOnly><AdminShell /></AdminOnly>}>
              <Route path="/admin" element={<AdminCosts />} />
              <Route path="/admin/credits" element={<AdminCredits />} />
              <Route path="/admin/packs" element={<AdminPacks />} />
              <Route path="/admin/costs" element={<Navigate to="/admin" replace />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="light" position="bottom-right" richColors />
        <ActionSheetHost />
      </AuthProvider>
    </div>
  );
}

export default App;
